/**
 * The viewer's own location scope at the selected clinic (migration 0161),
 * for DISPLAY only — every enforcement decision is server-side.
 *
 * Three things read it:
 *   - the header chip, so a shorter list reads as intended rather than as
 *     missing data;
 *   - the shared `LocationFilter`, which must not offer the "Unknown location"
 *     option to someone who was not granted it;
 *   - `useClinicLocations`' `isMultiLocation`, so a user scoped to one site
 *     sees the same collapsed UI as a genuinely single-site clinic.
 *
 * Plan: docs/plans/2026-09-20-user-location-scope.md
 */

import { useMemo } from 'react';
import { useAuth } from './useAuth';
// ⚠️ From the zero-dep conversation-engine leaf, NOT from `LocationFilter` —
// that component imports this hook, so reading the sentinel back from it would
// be an import cycle. The leaf is also the canonical definition the server and
// both SQL predicates use, so there is exactly one spelling of `'__needs__'`.
import { UNKNOWN_LOCATION_ID } from '@ruby/conversation-engine/utils/location-scope.js';

export interface ViewerLocationScope {
  /** True when the viewer is restricted at the selected clinic. */
  scoped: boolean;
  /** The real sites they hold. Empty when unscoped (use `scoped` to branch). */
  sites: string[];
  /** Whether the "Unknown location" bucket was granted. Unscoped ⇒ true. */
  unknown: boolean;
}

export function useLocationScope(): ViewerLocationScope {
  const { user, selectedClinic } = useAuth();
  return useMemo(() => {
    const entry = selectedClinic ? user?.locationScope?.[selectedClinic] : undefined;
    if (!Array.isArray(entry)) return { scoped: false, sites: [], unknown: true };
    return {
      scoped: true,
      sites: entry.filter((e) => e !== UNKNOWN_LOCATION_ID),
      unknown: entry.includes(UNKNOWN_LOCATION_ID),
    };
  }, [user?.locationScope, selectedClinic]);
}
